import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import axios from 'axios';
import i18next from 'i18next';
import { useEffect, useRef } from 'react';
import { generatePath, useLocation } from 'react-router-dom';
import { BaseError } from '~/components/Error/BaseError';
import {
  checkNoticeItemNewBadge,
  getTimestampForNotice,
} from '~/components/Support/Support.helpers';
import { CategoryType } from '../Notice/Notice.hooks';

export type NoticeModel = {
  open: number;
  close: number;
  sortOrder: number;
  title: string;
  revision: number;
  fmtRegistered: string;
  newBadge: boolean;
  registered: number;
  id: number;
  updated: number;
  category: CategoryType | string; // notice, event, winners
  subCategory?: string; // arcade, adventure
  originTitle?: string;
};

export type NoticePageModel = {
  nextSeq?: number;
  count: number;
  documents: NoticeModel[];
};

export type NoticeDetailModel = NoticeModel & { body: string };

export type NewCountModel = {
  newCount: number;
};

const LAN_URL = import.meta.env.VITE_LAN_URL;
const LAN_SERVICE_ID = import.meta.env.VITE_LAN_SERVICE_ID;
export const noticeListUrl = `${LAN_URL}/v1/${LAN_SERVICE_ID}/web/document/notice`;
export const noticeDetailUrl = `${LAN_URL}/v1/${LAN_SERVICE_ID}/web/document/notice/:id`;
export const noticeNewCountUrl = `${LAN_URL}/v1/${LAN_SERVICE_ID}/web/count/notice`;

export const entityDecode = (input: string) => {
  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.documentElement.textContent as string;
};

const extractCategories = (title: NoticeDetailModel['title']) => {
  const categories = title.toLowerCase().match(/\[(.*?)\]/g) || [];
  const [category = '', subCategory = ''] = categories.map((category) =>
    category.replace(/[\[\]]/g, '')
  );
  return { category, subCategory };
};
export const useInfiniteNotices = (options?: UseInfiniteQueryOptions<NoticePageModel>) =>
  useInfiniteQuery<NoticePageModel>(
    ['notice', 'list'],
    ({ pageParam }) =>
      axios
        .get<{ result: NoticePageModel }>(noticeListUrl, {
          params: { nextSeq: pageParam, lang: i18next.resolvedLanguage || 'en', size: 100 },
        })
        .then<NoticePageModel>(({ data }) => {
          const { result } = data;
          return {
            ...result,
            documents: result.documents.map((document) => {
              const { title } = document;
              const decodedTitle = entityDecode(title);
              const { category, subCategory } = extractCategories(title);
              return {
                ...document,
                title: decodedTitle.replace(/\[(.*?)\]/g, ''),
                originTitle: decodedTitle,
                category,
                subCategory,
                newBadge: checkNoticeItemNewBadge(document),
              };
            }),
          };
        }),
    {
      getNextPageParam: ({ nextSeq }) => nextSeq,
      refetchOnWindowFocus: false,
      ...options,
    }
  );

export const useInfiniteCategoryNotices = (
  category: CategoryType | string,
  options?: UseInfiniteQueryOptions<NoticePageModel>
) =>
  useInfiniteNotices({
    ...options,
    select: ({ pages, ...rest }) => ({
      ...rest,
      pages: getCategoryNoticeChunks(pages, category, 20),
    }),
  });

export const getCategoryNoticeChunks = (
  pages: NoticePageModel[],
  category: string,
  size: number
) => {
  const notices = pages.flatMap(({ documents }) => documents);
  const categoryNotices =
    category === 'all' ? notices : notices.filter((document) => document.category === category);

  const chunks: NoticePageModel[] = [];
  for (let i = 0; i < categoryNotices.length; i += size) {
    const nextChunk = categoryNotices.slice(i, i + size);
    chunks.push({
      documents: nextChunk,
      count: nextChunk.length,
    });
  }
  return chunks;
};

export const useNoticeDetail = ({ id }: { id: string }) => {
  const client = useQueryClient();
  const url = generatePath(noticeDetailUrl, { id });
  return useQuery<NoticeDetailModel>({
    queryKey: ['notice', 'detail', id],
    queryFn: () =>
      axios
        .get<{ result: NoticeDetailModel }>(url, { params: { lang: i18next.language || 'en' } })
        .then(({ data }) => {
          const { title } = data.result;
          const decodedTitle = entityDecode(title);
          const { category, subCategory } = extractCategories(title);

          return {
            ...data.result,
            title: decodedTitle.replace(/\[(.*?)\]/g, ''),
            originTitle: decodedTitle,
            category,
            subCategory,
          } as NoticeDetailModel;
        })
        .catch((e) => {
          throw new BaseError('NOT_FOUND', e.payload);
        }),
    initialData: () => {
      const cached = client.getQueryData<InfiniteData<NoticePageModel>>(['notice', 'list']);
      const data = cached?.pages
        .flatMap((page) => page.documents)
        .find((document) => document.id === +id);

      return {
        open: 0,
        close: 0,
        sortOrder: 0,
        title: '',
        revision: 0,
        fmtRegistered: '',
        newBadge: false,
        registered: 0,
        id: 0,
        updated: 0,
        category: '',
        subCategory: '',
        ...data,
      } as NoticeDetailModel;
    },
  });
};

export const useNoticeNewCount = (
  timestamp: number,
  options?: UseQueryOptions<NewCountModel>
): UseQueryResult<NewCountModel> =>
  useQuery<NewCountModel>(
    ['notice', 'newCount'],
    () =>
      axios
        .get<{ result: NewCountModel }>(noticeNewCountUrl, {
          params: { timestamp, lang: i18next.language || 'en' },
        })
        .then(({ data }) => data.result),
    {
      useErrorBoundary: false,
      ...options,
    }
  );

type UseNoticeNewBadgeType = {
  isFetch?: boolean;
  staleTime?: number;
};

export const useNoticeNewBadge = (
  { isFetch, staleTime }: UseNoticeNewBadgeType = { isFetch: true, staleTime: 1000 * 60 * 5 }
) => {
  const location = useLocation();
  const timestamp = getTimestampForNotice();
  const { data, refetch, isStale } = useNoticeNewCount(timestamp, {
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime,
  });

  const staleRef = useRef<boolean>();
  staleRef.current = isStale;

  useEffect(() => {
    if (isFetch) {
      staleRef.current && refetch();
    }
  }, [isFetch, location, refetch]);

  return { hasNoticeNewBadge: !!data?.newCount };
};
